import { useCallback, useRef, useEffect } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { AppNode, NodeType, NodeStatus } from '../types';
import { executeGeneration, GenerationContext } from '../services/generationStrategies';
import { toast } from 'sonner';

export function useGenerationOrchestrator(
    handleNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void,
    handleAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string) => void,
) {
    const nodes = useNodeStore((s) => s.nodes);
    const updateNode = useNodeStore((s) => s.updateNode);
    const currentProjectId = useProjectStore((s) => s.currentProjectId);
    const addTaskWithRequest = useTaskStore((s) => s.addTaskWithRequest);
    const removeTask = useTaskStore((s) => s.removeTask);
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const handleNodeAction = useCallback(async (id: string, promptOverride?: string) => {
        const node = nodesRef.current.find(n => n.id === id);
        if (!node) return;
        if (node.type === NodeType.MEDIA_UPLOAD) return;

        handleNodeUpdate(id, { error: undefined, isManualUpload: false });
        updateNode(id, {}, undefined, undefined, NodeStatus.WORKING);

        let currentTaskId: string | null = null;

        try {
            const inputs = node.inputs.map(i => nodesRef.current.find(n => n.id === i)).filter(Boolean) as AppNode[];

            const upstreamTexts = inputs.map(n => {
                if (n?.type === NodeType.PROMPT_INPUT) return n.data.prompt;
                if (n?.type === NodeType.VIDEO_ANALYZER) return n.data.analysis;
                return null;
            }).filter(t => t && t.trim().length > 0) as string[];

            let prompt = promptOverride || node.data.prompt || '';
            if (upstreamTexts.length > 0) {
                const combinedUpstream = upstreamTexts.join('\n');
                prompt = prompt ? `${combinedUpstream}\n${prompt}` : combinedUpstream;
            }

            const ctx: GenerationContext = {
                projectId: currentProjectId,
                onProgress: (nid, data) => handleNodeUpdate(nid, data),
                onAssetGenerated: handleAssetGenerated,
                onTaskCreated: (taskId, nodeId, nodeType, request) => {
                    currentTaskId = taskId;
                    addTaskWithRequest(taskId, nodeId, nodeType, request);
                    updateNode(nodeId, { taskId });
                },
            };

            const updates = await executeGeneration(node, inputs, prompt, ctx);
            handleNodeUpdate(id, updates);
            updateNode(id, {}, undefined, undefined, NodeStatus.SUCCESS);
            if (currentTaskId) removeTask(currentTaskId);
        } catch (e: any) {
            handleNodeUpdate(id, { error: e.message });
            updateNode(id, {}, undefined, undefined, NodeStatus.ERROR);
            if (currentTaskId) removeTask(currentTaskId);
            toast.error('生成失败', { description: e.message, duration: 6000 });
        }
    }, [handleNodeUpdate, updateNode, currentProjectId, handleAssetGenerated, addTaskWithRequest, removeTask]);

    return { handleNodeAction };
}
